
import React, { useState, useEffect } from "react";
import Select from "react-select";
import useFetchCountries from "../../hooks/useFetchCountries";
import { ApiUrl } from "../../../ApiUrl"; // Adjust path

const ParentDetailsForm = ({
  goToTab,
  addressDetails,
  setDocumentDetailsInParent,
}) => {
  // State to store form values
  const [formValues, setFormValues] = useState({
    residenceAddress: "",
    residenceCity: "",
    residenceState: "",
    residenceCountry: "Select",
    residencePincode: "",
    residencePhone: "",
    permanentAddress: "",
    permanentCity: "",
    permanentState: "",
    permanentCountry: "",
    permanentPincode: "",
    permanentPhone: "",
    sameAsResidence: false,
  });

  const { countries } = useFetchCountries();
  const [selectedCountry, setSelectedCountry] = useState(null); // Selected country state
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null); // Selected state
  const [cities, setCities] = useState([]);

  const [selectedResidenceDistrict, setSelectedResidenceDistrict] =
    useState(null);
  const [selectedPermanentState, setSelectedPermanentState] = useState(null);
  const [selectedPermanentDistrict, setSelectedPermanentDistrict] =
    useState(null);
  const [selectedPermanentCountry, setSelectedPermanentCountry] =
    useState(null);

  useEffect(() => {
    const requestData = {
      present_address: formValues.residenceAddress,
      present_country: selectedCountry?.value || null,
      present_state: selectedState?.value || null,
      present_district: selectedResidenceDistrict?.value || null,
      present_pincode: formValues.residencePincode,
      present_phone_number: formValues.residencePhone || "",
      permanent_address: formValues.permanentAddress,
      permanent_country: selectedPermanentCountry?.value || null,
      permanent_state: selectedPermanentState?.value || null,
      permanent_district: selectedPermanentDistrict?.value || null,
      permanent_pincode: formValues.permanentPincode || "",
      permanent_phone_number: formValues.permanentPhone || "",
    };

    sessionStorage.setItem(
      "staff_registration_address",
      JSON.stringify(requestData)
    );
  }, [
    formValues,
    selectedCountry,
    selectedState,
    selectedResidenceDistrict,
    selectedPermanentCountry,
    selectedPermanentState,
    selectedPermanentDistrict,
  ]);

  useEffect(() => {
    const initializeResidenceLocation = async () => {
      if (addressDetails && countries.length > 0) {
        setFormValues((prev) => ({
          ...prev,
          residenceAddress: addressDetails.present_address || "",
          residencePincode: addressDetails.present_pincode || "",
          residencePhone: addressDetails.present_phone_number || "",
          permanentAddress: addressDetails.permanent_address || "",
          permanentPincode: addressDetails.permanent_pincode || "",
          permanentPhone: addressDetails.permanent_phone_number || "",
        }));

        // Country (Residence)
        const presentCountryRaw = `${addressDetails.present_country ?? ""}`.toLowerCase();
        const matchedCountry = countries.find((c) => {
          const byDesc = `${c.countrydesc ?? ""}`.toLowerCase() === presentCountryRaw;
          const byCode = `${c.country_code ?? ""}`.toLowerCase() === presentCountryRaw;
          const byId = String(c.id) === String(addressDetails.present_country);
          return byDesc || byCode || byId;
        });

        if (matchedCountry) {
          const countryObj = {
            value: matchedCountry.id,
            label: matchedCountry.countrydesc,
          };
          setSelectedCountry(countryObj);

          // Fetch states
          const stateRes = await fetch(
            `${ApiUrl.apiurl}STATE/GetstatelistBasedOnCountryId/${countryObj.value}`
          );
          const stateJson = await stateRes.json();

          if (stateJson.message === "success") {
            const presentStateRaw = `${addressDetails.present_state ?? ""}`.toLowerCase();
            const matchedState = stateJson.data.find((s) => {
              const byDesc = `${s.stateDesc ?? ""}`.toLowerCase() === presentStateRaw;
              const byCode = `${s.state_code ?? ""}`.toLowerCase() === presentStateRaw;
              const byId = String(s.id) === String(addressDetails.present_state);
              return byDesc || byCode || byId;
            });
            if (matchedState) {
              const stateObj = {
                value: matchedState.id,
                label: matchedState.stateDesc,
              };
              setSelectedState(stateObj);

              // Fetch cities
              const cityRes = await fetch(
                `${ApiUrl.apiurl}CITY/GetCitylistBasedOnStateId/${stateObj.value}`
              );
              const cityJson = await cityRes.json();

              if (cityJson.message === "success") {
                const presentDistrictRaw = `${addressDetails.present_district ?? ""}`.toLowerCase();
                const matchedCity = cityJson.data.find((c) => {
                  const byName = `${c.city_name ?? ""}`.toLowerCase() === presentDistrictRaw;
                  const byId = String(c.id) === String(addressDetails.present_district);
                  return byName || byId;
                });
                if (matchedCity) {
                  setSelectedResidenceDistrict({
                    value: matchedCity.id,
                    label: matchedCity.city_name,
                  });
                }
              }
            }
          }
        }

        // Now set Permanent Address Details (if needed, use same cascade logic again)
        const permanentCountryRaw = `${addressDetails.permanent_country ?? ""}`.toLowerCase();
        const matchedPermanentCountry = countries.find((c) => {
          const byDesc = `${c.countrydesc ?? ""}`.toLowerCase() === permanentCountryRaw;
          const byCode = `${c.country_code ?? ""}`.toLowerCase() === permanentCountryRaw;
          const byId = String(c.id) === String(addressDetails.permanent_country);
          return byDesc || byCode || byId;
        });
        if (matchedPermanentCountry) {
          setSelectedPermanentCountry({
            value: matchedPermanentCountry.id,
            label: matchedPermanentCountry.countrydesc,
          });
        }
        setSelectedPermanentState({
          value: addressDetails.permanent_state,
          label: addressDetails.permanent_state,
        });
        setSelectedPermanentDistrict({
          value: addressDetails.permanent_district,
          label: addressDetails.permanent_district,
        });
      }
    };

    initializeResidenceLocation();
  }, [addressDetails, countries]);

  // Fetch states when a country is selected
  useEffect(() => {
    if (selectedCountry) {
      const fetchStates = async () => {
        try {
          const apiURL = `${ApiUrl.apiurl}STATE/GetstatelistBasedOnCountryId/${selectedCountry.value}`;
          const response = await fetch(apiURL);
          const result = await response.json();

          if (result.message === "success") {
            setStates(result.data); // Set the states data
          }
        } catch (err) {
          // No error handling is needed here as per your request
        }
      };

      fetchStates();
    }
  }, [selectedCountry]);

  // Fetch cities when a state is selected
  useEffect(() => {
    if (selectedState) {
      const fetchCities = async () => {
        try {
          const apiURL = `${ApiUrl.apiurl}CITY/GetCitylistBasedOnStateId/${selectedState.value}`;
          const response = await fetch(apiURL);
          const result = await response.json();

          if (result.message === "success") {
            setCities(result.data); // Set the cities data
          }
        } catch (err) {
          // No error handling is needed here as per your request
        }
      };

      fetchCities();
    }
  }, [selectedState]);

  const validatePincodeDynamic = async (pincode, districtName) => {
  if (!pincode || pincode.length !== 6 || !districtName) return true;

  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
    const data = await res.json();

    if (data[0]?.Status !== "Success") return false;

    return data[0].PostOffice?.some(
      (office) =>
        office.District?.toLowerCase() === districtName.toLowerCase()
    );
  } catch (error) {
    console.error("Pincode validation error:", error);
    return true;
  }
};

  const handleInputChange = (e) => {
  const { id } = e.target;
  let value = e.target.value;

  // Allow only numeric input for Pincode and Phone
  if (
    [
      "residencePincode",
      "permanentPincode",
      "residencePhone",
      "permanentPhone",
    ].includes(id)
  ) {
    value = value.replace(/\D/g, "");
  }

  // Pincode max 6 digits
  if (id === "residencePincode" || id === "permanentPincode") {
    value = value.slice(0, 6);
  }

  // Phone max 10 digits
  if (id === "residencePhone" || id === "permanentPhone") {
    value = value.slice(0, 10);
  }

  // Allow only text for Address fields
  if (id === "residenceAddress" || id === "permanentAddress") {
    if (!/^[A-Za-z\s,./-]*$/.test(value)) return;
  }

  setFormValues((prevValues) => ({
    ...prevValues,
    [id]: value,
  }));
};

  // Handle checkbox change
  const handleCheckboxChange = () => {
    setFormValues((prevValues) => {
      const isChecked = !prevValues.sameAsResidence;

      // If checked, copy residence fields to permanent fields
      return {
        ...prevValues,
        sameAsResidence: isChecked,
        ...(isChecked && {
          permanentAddress: prevValues.residenceAddress,
          permanentCity: prevValues.residenceCity,
          permanentState: prevValues.residenceState,
          permanentCountry: prevValues.residenceCountry,
          permanentPincode: prevValues.residencePincode,
          permanentPhone: prevValues.residencePhone,
        }),
      };
    });

    // Also copy the selected dropdown objects (for Select components)
    if (!formValues.sameAsResidence) {
      setSelectedPermanentState(selectedState);
      setSelectedPermanentDistrict(selectedResidenceDistrict);
      setSelectedPermanentCountry(selectedCountry); // if permanent country dropdown is using this
    } else {
      // If unchecked, reset them
      setSelectedPermanentState(null);
      setSelectedPermanentDistrict(null);
      setSelectedPermanentCountry(null);
    }
  };

  return (
    <div className="container-fluid">
      {/* Address Details */}
      <div className="row ">
        <div className="col-12">
          <div
            className="row mt-3 mx-2 custom-section-box"
            style={{ padding: "16px 16px 22px", minHeight: "560px" }}
          >
            <div className="col-12 col-md-6 d-flex">
              <div className="row g-2 flex-grow-1">
                <div className="col-12">
                  <h6
                    style={{
                      fontWeight: "700",
                      border: "1px solid #ccc",
                      textAlign: "center",
                      padding: "10px",
                      backgroundColor: "#007BFF",
                    }}
                  >
                    Residence Address
                  </h6>
                </div>

                <div className="col-12 mb-2">
                  <label htmlFor="residenceAddress" className="form-label">
                    Address
                  </label>
                  <input
                    type="text"
                    id="residenceAddress"
                    className="form-control detail"
                    placeholder="Enter address"
                    value={formValues.residenceAddress}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="country" className="form-label">
                    Country<span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    inputId="country"
                    className="detail"
                    classNamePrefix="residence-city-select"
                    options={countries.map((country) => ({
                      value: country.id,
                      label: country.countrydesc,
                    }))}
                    value={selectedCountry}
                    onChange={(selectedOption) =>
                      setSelectedCountry(selectedOption)
                    }
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="state" className="form-label">
                    State <span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    inputId="state"
                    className="detail"
                    classNamePrefix="residence-state-select"
                    options={states.map((state) => ({
                      value: state.id,
                      label: state.stateDesc,
                    }))}
                    onChange={(selectedOption) =>
                      setSelectedState(selectedOption)
                    }
                    value={selectedState}
                    isDisabled={!selectedCountry} // Disable if no country is selected
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="residenceCity" className="form-label">
                    City/District<span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    inputId="residenceCity"
                    className="detail"
                    classNamePrefix="residence-city-select"
                    options={cities.map((city) => ({
                      value: city.id,
                      label: city.city_name,
                    }))}
                    onChange={(selectedOption) =>
                      setSelectedResidenceDistrict(selectedOption)
                    }
                    value={selectedResidenceDistrict}
                    isDisabled={!selectedState}
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="residencePincode" className="form-label">
                    Pincode<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="residencePincode"
                    className="form-control detail"
                    placeholder="Enter pincode"
                    value={formValues.residencePincode}
                    onChange={handleInputChange}
                    onBlur={async () => {
  const isValid = await validatePincodeDynamic(
    formValues.residencePincode,
    formValues.residenceCity
  );

  if (!isValid) {
    alert("Pincode does not belong to the selected district");
    setFormValues((prev) => ({
      ...prev,
      residencePincode: "",
    }));
  }
}}
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="residencePhone" className="form-label">
                    Phone
                  </label>
                  <input
                    type="text"
                    id="residencePhone"
                    className="form-control detail"
                    placeholder="Enter phone number"
                    value={formValues.residencePhone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-12 mb-2">
                  <input
                    type="checkbox"
                    id="sameAsResidence"
                    checked={formValues.sameAsResidence}
                    onChange={handleCheckboxChange}
                  />
                  <label htmlFor="sameAsResidence" className="form-label" style={{ marginLeft: "8px" }}>
                    Permanent address is same as Residence
                  </label>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 d-flex">
              <div className="row g-2 flex-grow-1">
                <div className="col-12">
                  <h6
                    style={{
                      fontWeight: "700",
                      border: "1px solid #ccc",
                      textAlign: "center",
                      padding: "10px",
                      backgroundColor: "#007BFF",
                    }}
                  >
                    Permanent Address
                  </h6>
                </div>
                <div className="col-12 mb-2">
                  <label htmlFor="permanentAddress" className="form-label">
                    Address
                  </label>
                  <input
                    type="text"
                    id="permanentAddress"
                    className="form-control detail"
                    placeholder="Enter address"
                    value={formValues.permanentAddress}
                    onChange={handleInputChange}
                    disabled={formValues.sameAsResidence}
                  />
                </div>
                <div className="col-6 mb-2">
                  <label htmlFor="country" className="form-label">
                    Country
                  </label>
                  <Select
                    inputId="country"
                    className="detail"
                    classNamePrefix="residence-city-select"
                    options={countries.map((country) => ({
                      value: country.id,
                      label: country.countrydesc,
                    }))}
                    value={selectedPermanentCountry}
                    onChange={(selectedOption) =>
                      setSelectedCountry(selectedOption)
                    }
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="state" className="form-label">
                    State
                  </label>
                  <Select
                    inputId="permanentState"
                    className="detail"
                    classNamePrefix="residence-state-select"
                    options={states.map((state) => ({
                      value: state.id,
                      label: state.stateDesc,
                    }))}
                    onChange={(selectedOption) =>
                      setSelectedPermanentState(selectedOption)
                    }
                    value={selectedPermanentState}
                    isDisabled={!selectedCountry}
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="residenceCity" className="form-label">
                    City/District
                  </label>
                  <Select
                    inputId="permanentCity"
                    className="detail"
                    classNamePrefix="residence-city-select"
                    options={cities.map((city) => ({
                      value: city.id,
                      label: city.city_name,
                    }))}
                    onChange={(selectedOption) =>
                      setSelectedPermanentDistrict(selectedOption)
                    }
                    value={selectedPermanentDistrict}
                    isDisabled={!selectedPermanentState}
                  />
                </div>

                <div className="col-6 mb-2">
                  <label htmlFor="permanentPincode" className="form-label">
                    Pincode
                  </label>
                  <input
                    type="text"
                    id="permanentPincode"
                    className="form-control detail"
                    placeholder="Enter pincode"
                    value={formValues.permanentPincode}
                    onChange={handleInputChange}
                    onBlur={async () => {
  const isValid = await validatePincodeDynamic(
    formValues.permanentPincode,
    formValues.permanentCity
  );

  if (!isValid) {
    alert("Pincode does not belong to the selected district");
    setFormValues((prev) => ({
      ...prev,
      permanentPincode: "",
    }));
  }
}}
                    disabled={formValues.sameAsResidence}
                  />
                </div>
                <div className="col-6 mb-2">
                  <label htmlFor="permanentPhone" className="form-label">
                    Phone
                  </label>
                  <input
                    type="text"
                    id="permanentPhone"
                    className="form-control detail"
                    placeholder="Enter phone number"
                    value={formValues.permanentPhone}
                    onChange={handleInputChange}
                    disabled={formValues.sameAsResidence}
                  />
                </div>
                <div className="d-flex justify-content-between mb-3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentDetailsForm;